import useTransformedImageUrl from "@/shared/hooks/useTransformedImage";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { type KeyboardEvent } from "react";
import ThumbnailPreview from "./ThumbnailPreview";

interface ImageData {
  mediaId: string;
  cloudfrontUrl: string;
}

interface ImageModalProps {
  isOpen: boolean;
  images: ImageData[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onSelectImage: (index: number) => void;
}

const ImageModal = ({
  isOpen,
  images,
  currentIndex,
  onClose,
  onNext,
  onPrevious,
  onSelectImage,
}: ImageModalProps) => {
  // Transform current image URL
  const currentImage = images[currentIndex];
  const { transformedImageUrl } = useTransformedImageUrl(
    currentImage?.cloudfrontUrl,
  );
  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft") onPrevious();
    if (e.key === "ArrowRight") onNext();
  };

  if (!isOpen || images.length === 0) {
    return null;
  }

  return (
    <>
      {/* Overlay for the navbar - prevents clicks but keeps navbar visible */}
      <div
        className="fixed top-0 left-0 right-0 z-50 cursor-pointer"
        style={{ height: "76px", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
      />

      {/* Main modal container */}
      <div
        className="fixed left-0 right-0 bottom-0 z-40 flex items-center justify-center bg-white"
        style={{ top: "76px" }}
        onClick={onClose}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Close Button */}
        <div className="absolute top-4 right-4 z-20">
          <EmeraldIconButton
            icon="close"
            type="Icon"
            onClick={onClose}
            aria-label="Close modal"
          />
        </div>

        {/* Image Counter */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-gray-700 text-sm lg:text-base z-20">
          {currentIndex + 1}/{images.length}
        </div>

        {/* Previous Button */}
        {images.length > 1 && (
          <div className="absolute left-4 z-20">
            <EmeraldIconButton
              icon="chevron_left"
              type="Icon"
              onClick={(e) => {
                e.stopPropagation();
                onPrevious();
              }}
              aria-label="Previous image"
            />
          </div>
        )}

        {/* Image Display */}
        <div
          className="flex items-center justify-center w-full h-full"
          style={{
            padding: "5rem 6rem 8rem 6rem",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={transformedImageUrl}
            alt={`Property image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* Next Button */}
        {images.length > 1 && (
          <div className="absolute right-4 z-20">
            <EmeraldIconButton
              icon="chevron_right"
              type="Icon"
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              aria-label="Next image"
            />
          </div>
        )}

        {/* Thumbnail Preview */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] px-4">
          {images.map((image, index) => (
            <ThumbnailPreview
              key={image.mediaId}
              mediaId={image.mediaId}
              cloudfrontUrl={image.cloudfrontUrl}
              isSelected={index === currentIndex}
              onClick={(e) => {
                e.stopPropagation();
                onSelectImage(index);
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default ImageModal;
